// ----------------------------------------------------------------------------
// Snippet: Modal
// ----------------------------------------------------------------------------
//
// Modal poopup
//
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

// Styles
import './_modal.scss';

// -------------------------------------
// Class Constants
// -------------------------------------

const MODAL_OPEN_BUTTON  = '.modal__trigger';
const MODAL_CLOSE_BUTTON = '.modal__close-button';
const MODAL_OVERLAY      = '.modal__overlay';
const MODAL_CONTENT      = '.modal__popup .modal__content';
const VIDEO_BLOCK        = '.video-block';
const VIDEO              = '.video-block__video';

const OPEN_CLASS            = 'modal--open';
const BODY_OPEN_CLASS       = 'body-modal--open';

// -------------------------------------
// Functions
// -------------------------------------

/**
 * [OpenModal - Opens up the modal, locks body from scrolling, and adds overlay]
 * @param {HTMLElement} modal - The HTML element that represents the modal to be opened
 * @return void
 */
const openModal = (modal) => {
    const closeButton = modal.querySelector('.modal__close-button');
    // Open the Modal
    modal.classList.add(OPEN_CLASS);
    document.body.classList.add(BODY_OPEN_CLASS);

    // Lock document Body
    document.body.classList.add('noscroll');

    // Enable focus when opening modal
    modal.querySelectorAll('[tabindex="-1"]').forEach(modalContent => {
        modalContent.setAttribute('tabindex', '0');
    });

    closeButton.focus();

    // Broadcast a notification that a modal opened
    const modalEvent = new CustomEvent('open.modal', { detail: modal });
    window.dispatchEvent(modalEvent);
};

/**
 * [closeModal - Closes up the modal, un-position:fix's body, and removes overlay]
 * @return void
*/
const closeModal = () => {
    // Determine which modal(s) are opened
    const modal = document.querySelector('.modal--open');

    if (!modal) {
        return;
    }

    const modalId = modal.id.replace('modal_', '');
    const modalTrigger = document.querySelector(`[data-modal-target=${modalId}]`);

    modal.classList.remove(OPEN_CLASS);
    document.body.classList.remove(BODY_OPEN_CLASS);

    // Disable focus for modal content on close
    modal.querySelectorAll('[tabindex="0"]').forEach(modalContent =>{
        modalContent.setAttribute('tabindex', '-1');
    });

    // Broadcast a notification that a modal closed
    const modalEvent = new CustomEvent('close.modal', { detail: modal });
    window.dispatchEvent(modalEvent);

    modalTrigger.focus();

    // Unlock document Body
    document.body.classList.remove('noscroll');
};

/**
 * [triggerModal - Triggers Open Modal Function]
 * @param {Event} event - Action used on the element containing modal data.
 * @return void
 */
const triggerModal = (event) => {
    const modalId = event.currentTarget.dataset.modalTarget;
    const modal = document.querySelector(`#modal_${modalId}`);
    const modalContent = modal.querySelector(MODAL_CONTENT);

    // add all the elements inside modal which you want to make focusable
    const focusableElements     = modalContent.querySelectorAll('[tabindex]:not([tabindex="-1"]), [href], button, input, iframe');
    const firstFocusableElement = focusableElements[0]; // get first element to be focused inside modal
    const lastFocusableElement  = focusableElements[focusableElements.length - 1]; // get last element to be focused inside modal

    document.addEventListener('keydown', function(e) {
        const isTabPressed = e.key === 'Tab';

        if (!isTabPressed) {
            return;
        }

        if (e.shiftKey) { // if shift key pressed for shift + tab combination
            if (document.activeElement === firstFocusableElement) {
                e.preventDefault();
                lastFocusableElement.focus(); // add focus for the last focusable element
            }
        } else { // if tab key is pressed
            if (document.activeElement === lastFocusableElement) { // if focused has reached to last focusable element then focus first focusable element after pressing tab
                e.preventDefault();
                firstFocusableElement.focus(); // add focus for the first focusable element
            }
        }
    });

    openModal(modal);
};

// -------------------------------------
// Event Bindings
// -------------------------------------
document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll(MODAL_OPEN_BUTTON).forEach((openButton) => {
        openButton.addEventListener('click', triggerModal);
    });

    document.querySelectorAll(MODAL_CLOSE_BUTTON).forEach((closeButton) => {
        closeButton.addEventListener('click', closeModal);
    });

    document.querySelector(MODAL_OVERLAY)?.addEventListener('click', closeModal);

    // Open modal on keypress
    document.querySelectorAll(MODAL_OPEN_BUTTON).forEach((openButton) => {
        openButton.addEventListener('keypress', (event) => {
            if (event.key === 'Enter') {
                triggerModal(event);
            }
        });
    });
});

document.addEventListener('keydown', function(event) {
    if (event.key === 'Escape') { // Esc key
        closeModal();
    }
});


// When a video modal is opened, play the video
window.addEventListener('open.modal', (event) => {
    const modal = event.detail;
    const modalContent = modal.querySelector(MODAL_CONTENT);
    const videoBlock = modalContent.querySelector(VIDEO_BLOCK);

    if (videoBlock) {
        videoBlock.dispatchEvent(new Event('play.video-block'));
        videoBlock.querySelector(VIDEO).removeAttribute('inert');
    }
});

// When a video modal is closed, pause the video
window.addEventListener('close.modal', (event) => {
    const modal = event.detail;
    const modalContent = modal.querySelector(MODAL_CONTENT);
    const videoBlock = modalContent.querySelector(VIDEO_BLOCK);

    if (videoBlock) {
        videoBlock.dispatchEvent(new Event('pause.video-block'));
        videoBlock.querySelector(VIDEO).setAttribute('inert', '');
    }
});

export { openModal, closeModal, triggerModal };
